// THE LIST FRAME'S FUNCTIONS — components/AssistantViewList.js, the one
// read-only frame the built-in comms views mount (team, notifications,
// reminders, calls, a transcript, a chat channel, mail, a note).
//
//   assistantViewListLoad   the view's read (`viewLoad`), once per params,
//                           into the frame's OWN state
//   assistantViewListModel  the view's model (`viewModel`) over that read,
//                           normalised; null while reading
//   assistantViewListEmpty  the views contract's `empty`
//   assistantViewRow        a HomeWidgetRow item with every key it reads set
//   assistantViewGroup      one labelled list of rows
//
// Frank note: el.call serializes each export on its own (FA204) — every
// export here is self-contained.

// Run the view's read once per params. The loader answers `{ data, failed }`;
// a loader that throws is a failed read. A newer params set wins over a late
// answer to an older one.
export async function assistantViewListLoad(el) {
  const host = el || this
  if (!host || !host.state || typeof host.state.update !== 'function') return null
  const name = typeof host.viewLoad === 'string' ? host.viewLoad : ''
  if (!name) return null
  const params = (host.scope && host.scope.params) || {}
  let sig = name
  try {
    sig += ':' + JSON.stringify(params)
  } catch (_) {
    sig += ':?'
  }
  if (host.__assistantViewSig === sig) return null
  host.__assistantViewSig = sig
  let res = null
  try {
    res = await host.call(name, host, params)
  } catch (err) {
    console.warn('[assistantViewList] read failed:', name, err && err.message)
    res = { data: null, failed: true }
  }
  if (host.__assistantViewSig !== sig) return null
  const data = res && typeof res === 'object' && res.data != null ? res.data : {}
  // Frozen: the read is ONE leaf value for the state store (signal
  // store.js isLeafValue) — never a nested store per message or per person;
  // the models only read it, and a new read replaces it whole.
  host.state.update({
    viewData: typeof data === 'object' ? Object.freeze(data) : {},
    viewFailed: !!(res && res.failed)
  })
  return res
}

// The frame's model: the view's own over its read — `{ lead, groups, body,
// note, more }` with every field in its one shape; null while the read is
// out. `frame` is the view element (its `viewModel` and `scope.params`), `s`
// the state the caller reads.
export function assistantViewListModel(frame, s) {
  const host = frame || this
  const st = s || (host && host.state)
  if (!host || !st || typeof host.call !== 'function') return null
  const name = typeof host.viewModel === 'string' ? host.viewModel : ''
  if (!name || st.viewData == null) return null
  void (st.root && st.root.lang)
  const params = (host.scope && host.scope.params) || {}
  let m = null
  try {
    m = host.call(name, st.viewData, params, !!st.viewFailed)
  } catch (err) {
    console.warn('[assistantViewList] model failed:', name, err && err.message)
    return {
      lead: '',
      groups: [],
      body: '',
      note: 'workspace.assistantBlock.viewFailed',
      more: null
    }
  }
  if (!m || typeof m !== 'object') return null
  const text = (v) => (typeof v === 'string' ? v.trim() : '')
  const groups = (Array.isArray(m.groups) ? m.groups : []).filter(
    (g) => g && Array.isArray(g.groupRows) && g.groupRows.length
  )
  // `more`: { shown, total } — N of a known total; { shown, open: true } — a
  // list with a next page whose total the read does not know.
  let more = null
  if (m.more && typeof m.more === 'object') {
    const shown = Number(m.more.shown)
    const total = m.more.total == null ? null : Number(m.more.total)
    if (shown > 0 && total != null && total > shown) more = { shown, total }
    else if (shown > 0 && total == null && m.more.open === true) more = { shown, total: null }
  }
  return {
    lead: text(m.lead),
    groups,
    body: text(m.body),
    note: text(m.note),
    more
  }
}

// EMPTY (functions/assistantAppViews.js): true when the read answered and the
// frame shows only its one sentence; false with something to show; null
// while reading.
export function assistantViewListEmpty(frame, s) {
  const host = frame || this
  const m =
    host && typeof host.call === 'function' ? host.call('assistantViewListModel', host, s) : null
  if (!m) return null
  return !m.lead && !m.body && !m.groups.length
}

// One HomeWidgetRow item. Every key the row reads is set — a childrenAs
// 'state' row reads a key it does not hold from the ROOT state.
export function assistantViewRow(row) {
  const r = row && typeof row === 'object' ? row : {}
  const open = r.open && typeof r.open === 'object' && r.open.call ? r.open : null
  return {
    key: String(r.key || 'row'),
    title: r.title == null ? '' : String(r.title),
    meta: r.meta == null || r.meta === '' ? null : String(r.meta),
    initials: r.initials ? String(r.initials) : null,
    icon: r.icon ? String(r.icon) : null,
    src: r.src ? String(r.src) : null,
    progress: typeof r.progress === 'number' ? r.progress : null,
    bad: !!r.bad,
    mark: !!r.mark,
    skel: false,
    href: r.href ? String(r.href) : null,
    open: open ? { call: String(open.call), arg: open.arg === undefined ? null : open.arg } : null
  }
}

// One labelled list of rows (components/AssistantViewList.js
// AssistantViewGroup). The key is prefixed so a data word never names an
// HTML tag.
export function assistantViewGroup(group) {
  const g = group && typeof group === 'object' ? group : {}
  return {
    key: 'g-' + String(g.key || 'rows').replace(/[^a-zA-Z0-9_-]/g, ''),
    groupLabel: g.label ? String(g.label) : '',
    groupRows: Array.isArray(g.rows) ? g.rows : []
  }
}
